import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-1277e39e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ticket-search__section-wrapper" }
const _hoisted_2 = { class: "ticket-search__section" }
const _hoisted_3 = { class: "ticket-search__section__date" }
const _hoisted_4 = { class: "ticket-search__title" }
const _hoisted_5 = { class: "ticket-search__date-controls" }

import DatePicker from '@/components/DatePicker.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { computed, PropType, ref, watch } from 'vue'
import { StringDate } from '@/models/shared.model'
import { formatDateTime, formatServerDate } from '@/utils/formatters'
import { PublicServiceDetails } from '@/models/public/service.model'

import parseISO from 'date-fns/parseISO'
import { getValidationErrorData } from '@/utils/errors'
import isSameDay from 'date-fns/isSameDay'
import startOfToday from 'date-fns/startOfToday'
import { serviceResultCardCommonProps } from '@/enums/public'
import { debounce } from 'lodash'
import isBefore from 'date-fns/isBefore'
import { useFetchTicketServicePeriodAvailabilityMutation } from '@/queries/service/serviceTicket'
import { ITicketServicePeriodAvailabilityPayload } from '@/models/service/serviceTicket.model'
import { ALERT_TYPES, FORMATS, SIZES } from '@/enums/components'
import AlertBanner from '@/components/AlertBanner.vue'
import BaseButton from '@/components/BaseButton.vue'
import { YearMonth } from '@/models/calendar.model'
import {
  getNextYearMonth,
  getPreviousYearMonth,
  getYearMonthAsInterval,
  getYearMonthForDate,
} from '@/utils/calendar'

interface ISearchAvailableDatesOptions {
  yearMonth: YearMonth
  onSuccess: (data: ITicketServicePeriodAvailabilityPayload) => void
}

// Prefer debouncedSearchAvailableDates usage

export default /*#__PURE__*/_defineComponent({
  __name: 'TicketResultCardSearch',
  props: {
  selectedDate: {
    type: Date as PropType<Date | null>,
    default: null,
  },
  service: {
    type: Object as PropType<PublicServiceDetails>,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  groupService: serviceResultCardCommonProps.groupService,
},
  emits: ["update:selectedDate"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:selectedDate', selectedDate: Date | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





const selectedDate = ref<Date | null>(props.selectedDate)
const errors = ref<Record<string, unknown> | undefined>(undefined)
const calendarYearMonth = ref<YearMonth>(
  getYearMonthForDate(selectedDate.value),
)
// We use this to ignore calendarYearMonth watch callback when
// calendarYearMonth changes because of prev/next date button
const oldCalendarYearMonth = ref<YearMonth | null>(null)

const selectedStringDate = computed<StringDate | null>(() => {
  return selectedDate.value
    ? formatDateTime(selectedDate.value, 'yyyy-mm-dd')
    : null
})

/**
 * date picker's available dates based on availability endpoint information
 */
const availableStringDates = ref<StringDate[]>([])

/**
 * date picker's available dates based on availability endpoint information
 */
const availableDates = computed<Date[]>(() => {
  return availableStringDates.value.map(parseISO)
})

const { mutateAsync: fetchAvailableDates, isLoading: isAvailableDatesLoading } =
  useFetchTicketServicePeriodAvailabilityMutation()

const searchAvailableDates = async (settings: ISearchAvailableDatesOptions) => {
  errors.value = undefined
  const yearMonthInterval = getYearMonthAsInterval(settings.yearMonth)
  fetchAvailableDates({
    hashId: props.service.hash_id,
    body: {
      start: formatServerDate(yearMonthInterval.start),
      end: formatServerDate(yearMonthInterval.end),
    },
  })
    .then((data: ITicketServicePeriodAvailabilityPayload) => {
      settings.onSuccess(data)
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
}

const debouncedSearchAvailableDates = debounce(searchAvailableDates, 400)

const getDisabledDates = (date: Date) => {
  if (isAvailableDatesLoading.value) {
    return true
  }
  if (isBefore(date, startOfToday())) {
    return true
  }

  for (const availableDate of availableDates.value) {
    if (isSameDay(date, availableDate)) {
      return false
    }
  }
  return true
}

const selectedDateErrors = computed(() => {
  const _errors = []
  _errors.push(...(errors.value?.start ?? []))
  _errors.push(...(errors.value?.check_in ?? []))
  _errors.push(...(errors.value?.end ?? []))
  _errors.push(...(errors.value?.check_out ?? []))
  return _errors
})

const updateSelectedDate = (date: Date | null) => {
  selectedDate.value = date
}

const selectedDateIndex = computed(() => {
  return availableStringDates.value.findIndex(
    (date) => date === selectedStringDate.value,
  )
})

function selectPreviousAvailableDate() {
  errors.value = false
  if (!selectedDate.value) return null
  // Do we need to load next period because no available dates in current month
  // or because selected date is already the earliest available date in current month
  const noAvailableDates = availableDates.value.length === 0
  const isEarliestAvailableDateSelected = selectedDateIndex.value === 0
  if (noAvailableDates || isEarliestAvailableDateSelected) {
    const previousYearMonth = getPreviousYearMonth(calendarYearMonth.value)
    debouncedSearchAvailableDates({
      yearMonth: previousYearMonth,
      onSuccess: (data: ITicketServicePeriodAvailabilityPayload) => {
        oldCalendarYearMonth.value = previousYearMonth
        calendarYearMonth.value = previousYearMonth
        availableStringDates.value = data
        // Default to start of next period if no available date in given period
        selectedDate.value =
          availableDates.value[availableDates.value.length - 1] ??
          new Date(previousYearMonth.year, previousYearMonth.month, 1)
      },
    })
  } else {
    selectedDate.value = availableDates.value[selectedDateIndex.value - 1]
  }
}

function selectNextAvailableDate() {
  errors.value = false
  if (!selectedDate.value) return null
  // Do we need to load next period because no available dates in current month
  // or because selected date is already the last available date in current month
  const noAvailableDates = availableDates.value.length === 0
  const isLastAvailableDateSelected =
    selectedDateIndex.value === availableDates.value.length - 1
  if (noAvailableDates || isLastAvailableDateSelected) {
    const nextYearMonth = getNextYearMonth(calendarYearMonth.value)
    debouncedSearchAvailableDates({
      yearMonth: nextYearMonth,
      onSuccess: (data: ITicketServicePeriodAvailabilityPayload) => {
        oldCalendarYearMonth.value = nextYearMonth
        calendarYearMonth.value = nextYearMonth
        availableStringDates.value = data
        // Default to start of next period if no available date in given period
        selectedDate.value =
          availableDates.value[0] ??
          new Date(nextYearMonth.year, nextYearMonth.month, 1)
      },
    })
  } else {
    selectedDate.value = availableDates.value[selectedDateIndex.value + 1]
  }
}

watch(
  calendarYearMonth,
  () => {
    if (
      JSON.stringify(calendarYearMonth.value) ===
      JSON.stringify(oldCalendarYearMonth.value)
    ) {
      return
    }
    debouncedSearchAvailableDates({
      yearMonth: calendarYearMonth.value,
      onSuccess: (data: ITicketServicePeriodAvailabilityPayload) => {
        availableStringDates.value = data
      },
    })
  },
  { immediate: true },
)

watch(
  () => props.selectedDate,
  () => {
    selectedDate.value = props.selectedDate
  },
)

watch(selectedDate, (newVal) => {
  if (newVal !== props.selectedDate) {
    emit('update:selectedDate', newVal)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h4", _hoisted_4, _toDisplayString(_ctx.$t('whenAreYouGoing')), 1),
        (
            selectedDate.value &&
            !_unref(isAvailableDatesLoading) &&
            _unref(availableDates).length === 0
          )
          ? (_openBlock(), _createBlock(AlertBanner, {
              key: 0,
              text: _ctx.$t('noAvaiableDatesInMonth'),
              "alert-type": _unref(ALERT_TYPES).WARNING
            }, null, 8, ["text", "alert-type"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(BaseButton, {
            format: _unref(FORMATS).ICON_CIRCULAR,
            size: _unref(SIZES).SMALL,
            "icon-size": 32,
            disabled: !selectedDate.value || _unref(isAvailableDatesLoading),
            type: "button",
            icon: "chevron-left",
            onClick: selectPreviousAvailableDate
          }, null, 8, ["format", "size", "disabled"]),
          _createVNode(DatePicker, {
            "model-value": selectedDate.value,
            name: "selected-date",
            errors: _unref(selectedDateErrors),
            class: _normalizeClass(["ticket-search__input", {
              'ticket-search__input--error': _unref(selectedDateErrors).length > 0,
            }]),
            placeholder: _ctx.$t('date'),
            "disabled-dates": getDisabledDates,
            "month-change-on-scroll": false,
            "month-change-on-arrows": false,
            "keep-action-row": "",
            disabled: __props.disabled,
            "onUpdate:modelValue": updateSelectedDate,
            onUpdateMonthYear: _cache[0] || (_cache[0] = 
              (value) => {
                oldCalendarYearMonth.value = calendarYearMonth.value
                calendarYearMonth.value = value
              }
            )
          }, {
            "action-preview": _withCtx(() => [
              _createElementVNode("div", {
                class: _normalizeClass(["ticket-search__availabilities-loading", {
                  'visibility-hidden': !_unref(isAvailableDatesLoading),
                }])
              }, [
                _createVNode(LoadingSpinner, { size: 16 }),
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('bookingForm.loadingDatePickerTimeslots')), 1)
              ], 2)
            ]),
            "action-buttons": _withCtx(() => []),
            _: 1
          }, 8, ["model-value", "errors", "class", "placeholder", "disabled"]),
          _createVNode(BaseButton, {
            format: _unref(FORMATS).ICON_CIRCULAR,
            size: _unref(SIZES).SMALL,
            "icon-size": 32,
            disabled: !selectedDate.value || _unref(isAvailableDatesLoading),
            "icon-right": "",
            type: "button",
            icon: "chevron-right",
            onClick: selectNextAvailableDate
          }, null, 8, ["format", "size", "disabled"])
        ])
      ])
    ])
  ]))
}
}

})